import React from 'react';
import { GenerateInvoiceResponse } from '../../types/adminInvoice';

interface InvoiceGeneratePanelProps {
  selectedCount: number;
  generateLoading: boolean;
  generateError: string | null;
  generateSuccess: GenerateInvoiceResponse | null;
  onGenerate: () => void;
}

const InvoiceGeneratePanel: React.FC<InvoiceGeneratePanelProps> = ({
  selectedCount,
  generateLoading,
  generateError,
  generateSuccess,
  onGenerate,
}) => {
  const formatAmount = (amountDue?: number, currency?: string) => {
    if (amountDue == null) return null;
    const cur = (currency || 'usd').toUpperCase();
    const amount = amountDue / 100;
    if (cur === 'USD') return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    return `${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${cur}`;
  };

  return (
    <div className="border-t pt-6">
      {generateError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {generateError}
        </div>
      )}

      {generateSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium mb-2">Invoice generated successfully</p>
          {generateSuccess.amount_due != null && (
            <p className="text-sm text-green-700 mb-2">
              Total: {formatAmount(generateSuccess.amount_due, generateSuccess.currency)}
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            {generateSuccess.hosted_invoice_url && (
              <a
                href={generateSuccess.hosted_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                View invoice
              </a>
            )}
            {generateSuccess.invoice_pdf && (
              <a
                href={generateSuccess.invoice_pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Download PDF
              </a>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={onGenerate}
          disabled={selectedCount === 0 || generateLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {generateLoading && (
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          Generate Invoice
        </button>

        {selectedCount === 0 && (
          <span className="text-sm text-gray-500">Select at least one sport to generate an invoice</span>
        )}
      </div>
    </div>
  );
};

export default InvoiceGeneratePanel;
